// Copyright © 2026 Alex Bruce. All rights reserved.
import React, { useState, useMemo } from "react";
import type { Theme } from "../lib/theme";
import {
  filterNotes, repliesTo, parseMentions,
  type ProjectNote, type NoteAnchorKind, type Mentionable, type NewNote,
} from "../lib/projectNotes";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

export interface NotesAnchor {
  kind: NoteAnchorKind;
  id: string | null;
  label: string | null;
}

interface Props {
  T: Theme;
  notes: ProjectNote[];
  userId: string;
  people: Mentionable[];
  /** Set when opened from a row, so a new note lands on that line. */
  anchor: NotesAnchor | null;
  /** Set when the notes could not be loaded; shown instead of an empty list. */
  error?: string;
  onClose: () => void;
  onAdd: (note: Omit<NewNote, "projectId">) => Promise<void>;
  onResolve: (noteId: string, resolved: boolean) => Promise<void>;
  onDelete: (noteId: string) => Promise<void>;
}

const when = (iso: string) => {
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
};

const initials = (name: string) =>
  name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";

/**
 * The internal notes on an estimate.
 *
 * One list serves both jobs. Unresolved is an estimator's to-do list;
 * "mentions me" is an inbox. There is no separate task object because a note
 * with a lifecycle already is one.
 *
 * Deliberately not a chat: replies are one level deep and the resolve button
 * is the loudest control, because the artifact is the decision rather than
 * the conversation.
 */
export default function NotesPanel({
  T, notes, userId, people, anchor, error, onClose, onAdd, onResolve, onDelete,
}: Props) {
  const [status, setStatus] = useState<"open" | "resolved" | "all">("open");
  const [mine, setMine] = useState(false);
  const [mentioningMe, setMentioningMe] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // When opened from a row, start scoped to that row -- but say so, and let
  // it be dropped, or the panel looks empty for no visible reason.
  const [scoped, setScoped] = useState(!!anchor?.id);

  const visible = useMemo(() => {
    const base = filterNotes(notes, userId, { status, mine, mentioningMe, search });
    return scoped && anchor?.id ? base.filter(n => n.anchorId === anchor.id) : base;
  }, [notes, userId, status, mine, mentioningMe, search, scoped, anchor]);

  const mentionCount = useMemo(
    () => filterNotes(notes, userId, { status: "open", mentioningMe: true }).length,
    [notes, userId]);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await onAdd({
        body,
        anchorKind: anchor?.kind ?? "project",
        anchorId: anchor?.id ?? null,
        anchorLabel: anchor?.label ?? null,
        mentions: parseMentions(body, people),
      });
      setDraft("");
    } finally { setBusy(false); }
  };

  const submitReply = async (parentId: string) => {
    const body = replyDraft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await onAdd({ body, parentId, mentions: parseMentions(body, people) });
      setReplyDraft("");
      setReplyTo(null);
    } finally { setBusy(false); }
  };

  const chip = (on: boolean): React.CSSProperties => ({
    fontSize: 11, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontFamily: FONT,
    border: `1px solid ${on ? T.borderAccent : T.borderSecondary}`,
    background: on ? T.bgAccentSubtle : "none",
    color: on ? T.borderAccent : T.textMuted,
    fontWeight: on ? 700 : 400,
  });

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: T.bgModalBackdrop, zIndex: 9000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.bgPanel, borderLeft: `1px solid ${T.borderSecondary}`, width: 440, maxWidth: "100%",
          height: "100%", display: "flex", flexDirection: "column", fontFamily: FONT }}>

        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textSecondary }}>Notes</div>
            <div style={{ fontSize: 11, color: T.textWeak, marginTop: 2 }}>
              Internal only — these never appear on a client document.
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontFamily: FONT }}>
            <Icon.X size={12} />
          </button>
        </div>

        {/* Compose. At the top because adding is the common act. */}
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderSecondary}` }}>
          {anchor?.label && (
            <div style={{ fontSize: 10, color: T.borderAccent, marginBottom: 6, letterSpacing: ".04em" }}>
              on {anchor.label}
            </div>
          )}
          <textarea value={draft} onChange={e => setDraft(e.target.value)}
            placeholder={people.length ? "Add a note… use @name to tag someone" : "Add a note…"}
            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            style={{ width: "100%", boxSizing: "border-box", minHeight: 58, resize: "vertical", outline: "none",
              background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3,
              color: T.textPrimary, fontSize: 12, fontFamily: FONT, padding: "8px 10px" }} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={submit} disabled={!draft.trim() || busy}
              style={{ ...chip(true), opacity: !draft.trim() || busy ? 0.5 : 1, padding: "6px 14px" }}>
              Add note
            </button>
          </div>
        </div>

        {/* Filters: the to-do list and the inbox are two settings of one list. */}
        <div style={{ padding: "10px 18px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {(["open", "resolved", "all"] as const).map(s => (
            <button key={s} onClick={() => setStatus(s)} style={chip(status === s)}>
              {s === "open" ? "Open" : s === "resolved" ? "Resolved" : "All"}
            </button>
          ))}
          <span style={{ width: 1, height: 18, background: T.borderSecondary, margin: "0 2px" }} />
          <button onClick={() => setMine(v => !v)} style={chip(mine)}>Mine</button>
          <button onClick={() => setMentioningMe(v => !v)} style={chip(mentioningMe)}>
            Mentions me{mentionCount > 0 ? ` (${mentionCount})` : ""}
          </button>
          {anchor?.id && (
            <button onClick={() => setScoped(v => !v)} style={chip(scoped)}>
              This line only
            </button>
          )}
        </div>

        <div style={{ padding: "8px 18px", borderBottom: `1px solid ${T.borderSecondary}` }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search notes…"
            style={{ width: "100%", boxSizing: "border-box", background: T.bgInput, border: `1px solid ${T.borderSecondary}`,
              borderRadius: 3, color: T.textPrimary, fontSize: 11, fontFamily: FONT, padding: "6px 9px", outline: "none" }} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "12px 18px" }}>
          {error && (
            <div style={{ background: `${T.negative}14`, border: `1px solid ${T.negative}55`, borderRadius: 3,
              padding: "10px 12px", fontSize: 11, color: T.negative, marginBottom: 12, lineHeight: 1.5 }}>
              {error}
            </div>
          )}
          {!error && visible.length === 0 && (
            <div style={{ fontSize: 12, color: T.textMuted, padding: "28px 0", textAlign: "center" }}>
              {status === "open" ? "Nothing outstanding." : "No notes match."}
            </div>
          )}
          {visible.map(n => {
            const replies = repliesTo(notes, n.id);
            const resolved = !!n.resolvedAt;
            return (
              <div key={n.id}
                style={{ border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "10px 12px", marginBottom: 10,
                  background: resolved ? "none" : T.bgInput, opacity: resolved ? 0.65 : 1 }}>

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 22, height: 22, borderRadius: 999, background: T.bgAccentSubtle, color: T.borderAccent,
                    fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {initials(n.authorName)}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary }}>{n.authorName}</span>
                  <span style={{ fontSize: 10, color: T.textWeak }}>{when(n.createdAt)}</span>
                  <span style={{ flex: 1 }} />
                  <button onClick={() => onResolve(n.id, !resolved)}
                    title={resolved ? "Reopen" : "Mark resolved"}
                    style={{ ...chip(false), padding: "2px 8px", fontSize: 10,
                      color: resolved ? T.textWeak : T.positive, borderColor: resolved ? T.borderSecondary : `${T.positive}66` }}>
                    {resolved ? "Reopen" : "Resolve"}
                  </button>
                  {n.authorId === userId && (
                    <button onClick={() => onDelete(n.id)} title="Delete"
                      style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", padding: 2, fontFamily: FONT }}>
                      <Icon.Trash size={11} />
                    </button>
                  )}
                </div>

                {n.anchorLabel && (
                  <div style={{ fontSize: 10, color: T.borderAccent, marginBottom: 4 }}>{n.anchorLabel}</div>
                )}
                <div style={{ fontSize: 12, color: T.textPrimary, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{n.body}</div>

                {replies.map(r => (
                  <div key={r.id} style={{ marginTop: 8, paddingLeft: 10, borderLeft: `2px solid ${T.borderSecondary}` }}>
                    <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 2 }}>
                      <strong style={{ color: T.textTertiary }}>{r.authorName}</strong> · {when(r.createdAt)}
                    </div>
                    <div style={{ fontSize: 12, color: T.textTertiary, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{r.body}</div>
                  </div>
                ))}

                {replyTo === n.id ? (
                  <div style={{ marginTop: 8 }}>
                    <textarea value={replyDraft} autoFocus onChange={e => setReplyDraft(e.target.value)}
                      placeholder="Reply…"
                      onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitReply(n.id); }}
                      style={{ width: "100%", boxSizing: "border-box", minHeight: 44, resize: "vertical", outline: "none",
                        background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3,
                        color: T.textPrimary, fontSize: 12, fontFamily: FONT, padding: "6px 8px" }} />
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 6 }}>
                      <button onClick={() => { setReplyTo(null); setReplyDraft(""); }} style={{ ...chip(false), padding: "3px 10px", fontSize: 10 }}>Cancel</button>
                      <button onClick={() => submitReply(n.id)} disabled={!replyDraft.trim() || busy}
                        style={{ ...chip(true), padding: "3px 10px", fontSize: 10, opacity: !replyDraft.trim() || busy ? 0.5 : 1 }}>Reply</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setReplyTo(n.id); setReplyDraft(""); }}
                    style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 10, fontFamily: FONT, padding: "6px 0 0" }}>
                    Reply
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
